import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdKeyboardArrowDown } from 'react-icons/md';

const defaultGetOptionValue = (option = {}) =>
	String(option?.id ?? option?.value ?? option?.identificador ?? '');

const defaultGetOptionLabel = (option = {}) =>
	String(
		option?.nome_exibicao ||
			option?.nome_exibição ||
			option?.nome ||
			option?.identificador ||
			option?.label ||
			'',
	).trim();

const normalizeText = (value = '') =>
	String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

export default function SearchableSetorSelect({
	value,
	options = [],
	onChange,
	placeholder = 'Selecione o setor',
	disabled = false,
	className = '',
	style,
	getOptionValue = defaultGetOptionValue,
	getOptionLabel = defaultGetOptionLabel,
	emptyMessage = 'Nenhum setor encontrado',
	ariaLabel = 'Selecionar setor',
	menuPortal = false,
	clearDisplayWhenValue,
	showAllOptionsOnOpen = false,
}) {
	const containerRef = useRef(null);
	const menuRef = useRef(null);
	const listIdRef = useRef(
		`setor-options-${Math.random().toString(36).slice(2, 10)}`,
	);
	const [isOpen, setIsOpen] = useState(false);
	const [menuPosition, setMenuPosition] = useState(null);
	const selectedOption = useMemo(
		() =>
			(Array.isArray(options) ? options : []).find(
				(option) =>
					String(getOptionValue(option)) === String(value ?? ''),
			) || null,
		[options, value, getOptionValue],
	);
	const selectedLabel = selectedOption ? getOptionLabel(selectedOption) : '';
	const displayLabel =
		clearDisplayWhenValue != null &&
		String(value ?? '') === String(clearDisplayWhenValue)
			? ''
			: selectedLabel;
	const [searchTerm, setSearchTerm] = useState(displayLabel);
	const [isFiltering, setIsFiltering] = useState(false);

	useEffect(() => {
		if (!isOpen) setSearchTerm(displayLabel);
	}, [displayLabel, isOpen]);

	useEffect(() => {
		const closeWhenClickingOutside = (event) => {
			if (
				!containerRef.current?.contains(event.target) &&
				!menuRef.current?.contains(event.target)
			) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', closeWhenClickingOutside);
		return () =>
			document.removeEventListener('mousedown', closeWhenClickingOutside);
	}, []);

	useLayoutEffect(() => {
		if (!isOpen || !menuPortal || !containerRef.current) return undefined;
		const updateMenuPosition = () => {
			const rect = containerRef.current?.getBoundingClientRect();
			if (!rect) return;
			setMenuPosition({
				top: rect.bottom + 4,
				left: rect.left,
				width: rect.width,
			});
		};
		updateMenuPosition();
		window.addEventListener('resize', updateMenuPosition);
		window.addEventListener('scroll', updateMenuPosition, true);
		return () => {
			window.removeEventListener('resize', updateMenuPosition);
			window.removeEventListener('scroll', updateMenuPosition, true);
		};
	}, [isOpen, menuPortal]);

	const filteredOptions = useMemo(() => {
		const allOptions = Array.isArray(options) ? options : [];
		const term = normalizeText(searchTerm);
		const isShowingAllOptions =
			showAllOptionsOnOpen && isOpen && !isFiltering;
		if (!term) return allOptions;

		const includesTerm = (option) => {
			const label = normalizeText(getOptionLabel(option));
			const optionValue = normalizeText(getOptionValue(option));
			return label.includes(term) || optionValue.includes(term);
		};

		if (!isShowingAllOptions) return allOptions.filter(includesTerm);

		return [
			...allOptions.filter(includesTerm),
			...allOptions.filter((option) => !includesTerm(option)),
		];
	}, [
		options,
		searchTerm,
		getOptionLabel,
		getOptionValue,
		isFiltering,
		isOpen,
		showAllOptionsOnOpen,
	]);

	const selectOption = (option) => {
		const optionValue = String(getOptionValue(option));
		onChange?.(optionValue, option);
		setSearchTerm(getOptionLabel(option));
		setIsFiltering(false);
		setIsOpen(false);
	};

	const openList = () => {
		setIsFiltering(false);
		setIsOpen(true);
	};

	const optionsMenu = isOpen && !disabled ? (
		<div
			ref={menuRef}
			id={listIdRef.current}
			className={`${
				menuPortal
					? 'fixed z-[9999]'
					: 'absolute left-0 right-0 top-full z-50 mt-1'
			} max-h-[220px] overflow-y-auto rounded-[8px] border border-[#E3E3E3] bg-white shadow-md`}
			style={menuPortal ? menuPosition || { visibility: 'hidden' } : undefined}>
			{filteredOptions.length ? (
				filteredOptions.map((option) => {
					const optionValue = String(getOptionValue(option));
					return (
						<button
							type='button'
							key={optionValue}
							onMouseDown={(event) => {
								event.preventDefault();
								selectOption(option);
							}}
							className={`block w-full px-3 py-2 text-left text-[13px] transition-colors hover:bg-[#F3F4F6] ${
								optionValue === String(value ?? '')
									? 'bg-[#EEF4FA] text-[#004A80]'
									: 'text-[#4F5257]'
							}`}>
							{getOptionLabel(option)}
						</button>
					);
				})
			) : (
				<div className='px-3 py-2 text-[13px] text-[#9CA3AF]'>
					{emptyMessage}
				</div>
			)}
		</div>
	) : null;

	return (
		<div ref={containerRef} className='relative w-full'>
			<input
				type='text'
				value={searchTerm}
				disabled={disabled}
				onFocus={() => !disabled && openList()}
				onClick={() => !disabled && openList()}
				onChange={(event) => {
					setSearchTerm(event.target.value);
					setIsFiltering(true);
					setIsOpen(true);
				}}
				placeholder={placeholder}
				aria-label={ariaLabel}
				aria-expanded={isOpen}
				aria-controls={listIdRef.current}
				role='combobox'
				className={`w-full pr-9 ${className}`}
				style={style}
			/>
			<button
				type='button'
				disabled={disabled}
				onMouseDown={(event) => event.preventDefault()}
				onClick={() =>
					!disabled &&
					setIsOpen((current) => {
						if (!current || isFiltering) {
							setIsFiltering(false);
							return true;
						}
						return false;
					})
				}
				aria-label={isOpen ? 'Fechar lista de setores' : 'Abrir lista de setores'}
				className='absolute inset-y-0 right-0 flex w-9 items-center justify-center text-[#7A8796] disabled:cursor-not-allowed'>
				<MdKeyboardArrowDown
					size={20}
					className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
				/>
			</button>

			{menuPortal && typeof document !== 'undefined'
				? createPortal(optionsMenu, document.body)
				: optionsMenu}
		</div>
	);
}
